import { apiServiceWrapper } from '../../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../../Utils/Auth/AuthToken';
import {
  AUTEST_EXECUTIONS,
  AUTEST_ANALYTICS,
  AUTEST_TEST_REPORT,
} from '../../../../constants/apiRoutes';
import {
  getGraphqlCacheResponse,
  setGraphqlCacheResponse,
} from '../../../../Utils/Storage/IndexDB/indexDbWrappers';
/**
 * list Executions.
 * @returns {Promise} - A promise that resolves with the list of Executions or rejects with an error.
 * @param props
 */
export const listExecution = async ({
  accessToken,
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const data = {
    Filters: filter,
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: limit,
    sortField: sort,
    Attributes: [
      {
        AttributeName: 'ChildRequired',
        AttributeValue: 'no',
      },
    ],
  };

  const response = await apiServiceWrapper({
    url: `${AUTEST_EXECUTIONS}/Filter`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: data,
    accessToken,
  });
  if (response?.isError) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker,
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }
  return {
    executions: response.Payload,
    TotalCount: response?.TotalRecordCount,
  };
};

/**
 * download the Execution.
 * @returns {Promise} - A promise that resolves with the download of Execution or rejects with an error.
 * @param data
 */
export const downloadExecution = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();

  const tempData = {
    Filters: [
      {
        ColumnName: 'uc_ossi_run_set_grp_seq',
        Op: 'EQ',
        ColumnValue: data?.uc_ossi_run_set_grp_seq,
      },
    ],
    pageNumber: 1,
    pageSize: 50,
    sort: [],
    Attributes: [
      {
        AttributeName: 'ChildRequired',
        AttributeValue: 'yes',
      },
      {
        AttributeName: "summary",
        AttributeValue:"yes"
      },
    ],
  };
  const response = await apiServiceWrapper({
    url: `${AUTEST_TEST_REPORT}`,
    method: 'POST',
    body: tempData,
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  return response;
};

/**
 * download the Execution Detailed Report.
 * NOTE: This is intentionally a copy of `downloadExecution` for now.
 * You can later update endpoint / payload / attributes for the detailed report.
 * @returns {Promise} - A promise that resolves with the download of Execution detailed report or rejects with an error.
 * @param data
 */
export const downloadExecutionDetailed = async ({
  data,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const { accessToken } = getTokensFromStorage();

  const tempData = {
    Filters: [
      {
        ColumnName: 'uc_ossi_run_set_grp_seq',
        Op: 'EQ',
        ColumnValue: data?.uc_ossi_run_set_grp_seq,
      },
    ],
    pageNumber: 1,
    pageSize: 50,
    sort: [],
    Attributes: [
      {
        AttributeName: 'ChildRequired',
        AttributeValue: 'yes',
      },
      {
        AttributeName: 'attachment',
        AttributeValue: 'yes',
      },
    ],
  };

  const response = await apiServiceWrapper({
    url: `${AUTEST_TEST_REPORT}`,
    method: 'POST',
    body: tempData,
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
  });
  return response;
};

//#region Execution Analytics

/**
 * get Execution Analytics for the drill down.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ListExecutionAnalytics or rejects with an error.
 * @param props
 */
export const ListExecutionAnalytics = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/Executions`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionsAnalytics: response.Payload,
  };
};

/**
 * get Execution Analytics for ExecutionsSuccessFailure.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ExecutionsSuccessFailure or rejects with an error.
 * @param props
 */
export const getExecutionsSuccessFailure = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionsSuccessFailure`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionsSuccessFailure: response.Payload,
  };
};

/**
 * get Execution Analytics for ExecutionsByModule.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ExecutionsByModule or rejects with an error.
 * @param props
 */
export const getExecutionsByModule = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionsByModule`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionsByModule: response.Payload,
  };
};

/**
 * get Execution Analytics for ExecutionTypeData.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ExecutionTypeData or rejects with an error.
 * @param props
 */
export const getExecutionTypeData = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionTypeData`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    invokeMethodTests: response.Payload,
  };
};

/**
 * get Execution Analytics for ExecutionsByTestType.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ExecutionsByTestType or rejects with an error.
 * @param props
 */
export const getExecutionsByTestType = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionsByTestType`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionsRunByType: response.Payload,
  };
};

/**
 * get Execution Analytics for ExecutionTime.
 * @returns {Promise} - A promise that resolves with the list of Execution Analytics for ExecutionTime or rejects with an error.
 * @param props
 */
export const getExecutionTime = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionTime`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionTime: response.Payload,
  };
};

//#endregion

//#region Autest Analytics

/**
 * get Autest Analytics for TotalCounts.
 * @returns {Promise} - A promise that resolves with the list of Autest Analytics for TotalCounts or rejects with an error.
 * @param props
 */
export const getTotalCounts = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/TotalCounts`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    analyticsData: response.Payload,
  };
};

/**
 * get Autest Analytics for ExecutionResultSummary.
 * @returns {Promise} - A promise that resolves with the list of Autest Analytics for ExecutionResultSummary or rejects with an error.
 * @param props
 */
export const getExecutionResultSummary = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionResultSummary`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    executionsPassFail: response.Payload,
  };
};

/**
 * get Autest Analytics for ExecutionTotal.
 * @returns {Promise} - A promise that resolves with the list of Autest Analytics for ExecutionTotal or rejects with an error.
 * @param props
 */
export const getExecutionTotal = async ({
  moduleName,
  isServiceWorker,
  listController,
  data,
}) => {
  const { accessToken } = getTokensFromStorage();
  const response = await apiServiceWrapper({
    url: `${AUTEST_ANALYTICS}/ExecutionTotal`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    accessToken,
    body: data,
  });
  return {
    ExecutionTotal: response.Payload,
  };
};

//#endregion
